import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, Container, Button, Icon } from '@mui/material';
import { Link } from 'react-router-dom';
import { fetchPeople, selectPeople } from 'app/store/marketing/peopleSlice';
import Person from 'app/shared-components/marketing/Person';
import HeaderTags from 'app/shared-components/helmet/HeaderTags';
import UserList from 'app/shared-components/user/UserList';
import CTASection from '../shared-components/CTASection';
import TrustBadges from '../shared-components/TrustBadges';
import { apiGet } from 'app/shared-components/util/restAPI';


const ABOUTS = [
  {
    title: 'Why We Exist',
    body: 'Care happens between visits, but most platforms leave providers isolated. Halespring creates a connected ecosystem where therapists, psychiatrists, and families collaborate in real-time, ensuring no patient falls through the cracks.',
    icon: 'help'
  },
  {
    title: 'How We Help',
    body: 'Connection drives everything we do. By connecting data through AI, connecting providers through referrals, and connecting care teams through collaboration, we help you build a thriving practice and deliver superior patient outcomes.',
    icon: 'favorite_border'
  }
];

export default function About() {
  const dispatch = useDispatch();
  const people = useSelector(selectPeople());
  const [clinicalAdvisors, setClinicalAdvisors] = useState([]);
  const host = window.location.protocol + '//' + window.location.host;
  const orgImage=  host + '/assets/images/logo/HALESPRING-HEADER-191.jpg';
  
  useEffect(() => {
    dispatch(fetchPeople({
      sort: ['createdAt:asc']
    }));
    fetchClinicalAdvisors();
  }, []);

  async function fetchClinicalAdvisors() {
    try {
      const apiName = 'PublicAPI2';
      const path = '/x2/indexedlistitem/public/clinicaladvisors';
      const options = {
        headers: {},
        response: false
      };
      const advisors = await apiGet(apiName, path, options);
      setClinicalAdvisors(advisors.reverse());
    } catch (error) {
      console.error('Failed to fetch clinical advisors:', error);
    }
  }

  return (
    <>
      <HeaderTags title="About Halespring: our mission and team" 
                  description="Learn more about our mission, who we are, and what we do." 
                  keywords="Halespring,Team,Mental Health,Therapist,Therapy,Community,Referrals,Pyschologist,Psychiatrist"
                  image={orgImage}
      />
    <div className="min-h-screen">
      {/* HERO SECTION */}
      <section className="pt-24 pb-20 bg-gradient-to-b from-blue-50 to-white">
        <Container maxWidth="lg">
          <div className="text-center max-w-4xl mx-auto">
            <Typography
              variant="h1"
              align="center"
              className="text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight"
            >
              About{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Halespring
              </span>
            </Typography>

            <div className="inline-block mb-6 px-4 py-1.5 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm tracking-wide uppercase">
              Empowering Mental Health Care
            </div>

            <Typography
              variant="h2"
              align="center"
              className="text-xl text-gray-600 mb-10 font-normal leading-relaxed max-w-2xl mx-auto"
            >
              Transforming the mental health landscape with secure, HIPAA-compliant tools for providers, patients, and families.
            </Typography>

            <div className="flex items-center justify-center gap-8 opacity-90 grayscale hover:grayscale-0 transition-all duration-500">
              <TrustBadges />
            </div>
          </div>
        </Container>
      </section>

      {/* ABOUT */}
      <section className="py-24 bg-gray-50 shadow-inner">
        <Container maxWidth="lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {ABOUTS.map((about, index) => (
              <div key={index} className="bg-white pt-20 pb-16 px-16 rounded-2xl border border-gray-200 hover:shadow-xl hover:border-blue-200 transition-all duration-300 group">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 mx-auto bg-blue-100 text-blue-600 group-hover:scale-110 transition-transform duration-300">
                    <Icon sx={{ fontSize: 40 }}>{about.icon}</Icon>
                  </div>
                  <Typography variant="h4" className="font-bold text-gray-900 mb-6 text-center">
                    {about.title}
                  </Typography>
                  <Typography variant="body1" className="text-gray-600 leading-relaxed text-xl text-center">
                    {about.body}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* TEAM */}
      <section className="py-24 bg-white">
        <Container maxWidth="lg">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Meet Our Team
            </Typography>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {people && people.filter((person) => person.type === 'team_member').map((person) => (
              <Person
                key={person.id}
                person={person}
                className="bg-hs-blue-pale"
              />
            ))}
          </div>
        </Container>
      </section>

      {/* ADVISORS */}
      <section className="py-24 bg-gray-50">
        <Container maxWidth="lg">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Board of Advisors
            </Typography>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {people && people.filter((person) => person.type === 'advisor').map((person) => (
              <Person
                key={person.id}
                person={person}
                className="bg-hs-grey-light"
              />
            ))}
          </div>
        </Container>
      </section>

      {/* CLINICAL ADVISORS */}
      {clinicalAdvisors && clinicalAdvisors.length > 0 && (
        <section className="py-24 bg-white">
          <Container maxWidth="lg">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Clinical Advisory Council
              </Typography>
            </div>

            <UserList 
              users={clinicalAdvisors}
              emptyMessage="No clinical advisors found"
              singleColumn={false}
              onClickBasePath="/public/profile/"
            />
          </Container>
        </section>
      )}

      {/* FINAL CTA SECTION */}
      <CTASection
        headline="Ready to Join Our Mission?"
        copy="Be part of transforming mental healthcare. Connect with our community of providers and patients working together for better outcomes."
        primaryCTA="Get Started Free"
        secondaryCTA="Learn More"
        trustElements={[
          "HIPAA-compliant platform",
          "Trusted by professionals",
          "Free to get started",
          "Built by clinicians"
        ]}
      />
    </div>
    </>
  )
}